/**
 * AppEnv.ts
 * Environment classifier separating the DEV build (testing, GPS simulator, quick cheat tools)
 * from the PRODUCTION build (clean commercial gameplay for end-users).
 */

export type AppEnvMode = 'dev' | 'prod';

let manualOverride: AppEnvMode | null = null;

export function setAppEnvOverride(mode: AppEnvMode | null): void {
  manualOverride = mode;
}

export function getAppEnv(): AppEnvMode {
  if (manualOverride) {
    return manualOverride;
  }

  // 1. Check URL query parameter override (?dev=1 or ?prod=1) and hostname (dev.*.pages.dev)
  if (typeof window !== 'undefined' && window.location) {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('dev') === '1' || params.get('env') === 'dev') {
        return 'dev';
      }
      if (params.get('prod') === '1' || params.get('env') === 'prod') {
        return 'prod';
      }

      const host = (window.location.hostname || '').toLowerCase();
      if (host.startsWith('dev.') || host.includes('-dev.')) {
        return 'dev';
      }
    } catch {
      // Ignore URL parsing errors in headless environments
    }
  }

  // 2. Check Vite build-time environment variable VITE_APP_ENV
  try {
    const viteEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
    if (viteEnv?.VITE_APP_ENV === 'prod') {
      return 'prod';
    }
    if (viteEnv?.VITE_APP_ENV === 'dev') {
      return 'dev';
    }
  } catch {
    // Fallback to dev
  }

  return 'dev';
}

export function isDevMode(): boolean {
  return getAppEnv() === 'dev';
}

export function isProdMode(): boolean {
  return getAppEnv() === 'prod';
}
